import { CREATE_DATA_OBJECT, RECEIVE_DATA_OBJECTS } from '@inpart-io/devtools-vue';
import { DataType, privateTasksManager } from '@inpart-io/padme-iap';
import { type IEvaluation, type IPrivateTask, IPrivateTaskStatus, IPrivateTaskType } from '@inpart-io/padme-iap';
import { type ActionTree } from 'vuex';

import { SAVE_SEARCH_RESULT } from '@/store/base/search/actions';
import { GET_SEARCH_RESULT_ID_LIST } from '@/store/base/search/getters';
import { EVALUATION_MODULE } from '@/store/evaluation-module/module';

export const CHANGE_STATUS = 'changeStatus';
export const REORDER_TASKS = 'reorderTasks';
export const STORE_DATA_OBJECTS = 'storeDataObjects';

export const CREATE_PRIVATE_TASKS = 'createPrivateTasks';

export const actions: ActionTree<IPrivateTask, unknown> = {
    async [CHANGE_STATUS](context, { dobjId, status, properties }: { dobjId: number; status: IPrivateTaskStatus; properties?: string }) {
        let result: IPrivateTask;
        switch (status) {
            case IPrivateTaskStatus.ACTIVE:
                result = await privateTasksManager.changeStatusToActive(dobjId, { properties });
                break;
            case IPrivateTaskStatus.COMPLETED:
                result = await privateTasksManager.changeStatusToCompleted(dobjId, { properties });
                break;
            case IPrivateTaskStatus.SKIPPED:
                result = await privateTasksManager.changeStatusToSkipped(dobjId, { properties });
                break;
            case IPrivateTaskStatus.IN_PROGRESS:
                result = await privateTasksManager.changeStatusToInProgress(dobjId, { properties });
                break;
        }
        if (result !== undefined) {
            void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [result] });
        }
    },
    async [REORDER_TASKS](
        context,
        { key, linkId, linkType, idList, properties }: { key: string; linkId: number; linkType: string; idList: number[]; properties?: string }
    ) {
        void context.dispatch(SAVE_SEARCH_RESULT, { key, idList });
        const results: IPrivateTask[] = await privateTasksManager.reorderTasks(linkId, linkType, idList, { properties });
        if (results?.length > 0) {
            void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: results });
            // For now only simple tasks can be reordered, to allow evaluation reorder delete this
            const resultsIdList = results
                .filter((res: IPrivateTask) => {
                    return res.type === IPrivateTaskType.PRIVATE_TASK;
                })
                .map((task: IPrivateTask) => {
                    return task.dobjId;
                });
            void context.dispatch(SAVE_SEARCH_RESULT, { key, idList: resultsIdList });
        }
    },

    async [CREATE_PRIVATE_TASKS](context, { key, dataObject, properties }: { key: string; dataObject: IPrivateTask; properties?: string }) {
        const newTaskIds: number[] = await context.dispatch(CREATE_DATA_OBJECT, { dataObjectList: [dataObject], properties });
        if (newTaskIds?.length > 0) {
            const idList: number[] = context.getters[GET_SEARCH_RESULT_ID_LIST](key) ?? [];
            idList.unshift(...newTaskIds);
            void context.dispatch(SAVE_SEARCH_RESULT, { key, idList });
        }
    },
    [STORE_DATA_OBJECTS](context, { dataObjectList }: { dataObjectList: IPrivateTask[] }) {
        const tasks: IPrivateTask[] = [];
        const evaluations: IEvaluation[] = [];
        dataObjectList.forEach((task) => {
            let _task = Object.assign({}, task);
            // Extract evaluations objects if necessary
            if (task['@evaluations']?.length > 0) {
                const evaluationIdList: number[] = [];
                task['@evaluations'].forEach((evaluation: number | IEvaluation) => {
                    if (evaluation instanceof Object && evaluation?.dobjId !== null && evaluation?.dobjId !== undefined) {
                        evaluations.push(Object.assign({}, evaluation, { '@type': DataType.Evaluation as DataType.Evaluation }));
                    }
                    evaluationIdList.push(evaluation instanceof Object ? evaluation.dobjId : evaluation);
                });
                _task = Object.assign({}, task, { '@evaluations': evaluationIdList });
            }
            tasks.push(_task);
        });
        if (tasks.length > 0) {
            context.commit(RECEIVE_DATA_OBJECTS, { dataObjectList: tasks });
        }
        if (evaluations.length > 0) {
            void context.dispatch(`${EVALUATION_MODULE}/${STORE_DATA_OBJECTS}`, { dataObjectList: evaluations }, { root: true });
        }
    }
};
